import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClientTeam } from "@/features/clients/hooks/use-clients";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import type { CustomFieldProps } from "./dynamic-form";

type SmsAlertDestination =
  | { kind: "member"; member_id: string }
  | { kind: "phone"; number: string };

const NONE = "none";

/**
 * Who is texted when a Messagerie request cannot wait
 * (`messages.sms_alert_destination`): nobody, a team member (their mobile) or a
 * number typed here. Server-side, only an international number is accepted.
 */
export function SmsAlertField({
  fieldName,
  fieldSchema,
  value,
  onChange,
}: CustomFieldProps) {
  const { t } = useTranslation("tools");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: team } = useClientTeam(clientId || "");

  const base = "tools.catalog.messages.fields.sms_alert_destination";
  const destination = (value as SmsAlertDestination | null | undefined) || null;
  const members = (team?.members || []).filter((m) => m.membership?.phone);
  const memberKey = (m: (typeof members)[number]) =>
    m.user?.id || m.membership.id;

  const current =
    destination?.kind === "member"
      ? `member:${destination.member_id}`
      : destination?.kind === "phone"
        ? "phone"
        : NONE;
  const known =
    destination?.kind !== "member" ||
    members.some((m) => memberKey(m) === destination.member_id);

  const select = (next: string) => {
    if (next === NONE) return onChange(null);
    if (next === "phone") return onChange({ kind: "phone", number: "" });
    onChange({ kind: "member", member_id: next.slice("member:".length) });
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName}>{fieldSchema.title || fieldName}</Label>
      <Select value={current} onValueChange={select}>
        <SelectTrigger id={fieldName} className="md:w-96">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{t(`${base}.none`)}</SelectItem>
          {!known && (
            <SelectItem value={current}>{t(`${base}.unknown`)}</SelectItem>
          )}
          {members.map((m) => (
            <SelectItem key={memberKey(m)} value={`member:${memberKey(m)}`}>
              {m.membership.display_name ||
                m.user?.full_name ||
                m.user?.email ||
                m.membership.phone}
            </SelectItem>
          ))}
          <SelectItem value="phone">{t(`${base}.phone`)}</SelectItem>
        </SelectContent>
      </Select>
      {destination?.kind === "phone" && (
        <Input
          type="tel"
          className="md:w-96"
          placeholder="+33 6 12 34 56 78"
          value={destination.number}
          onChange={(event) =>
            onChange({ kind: "phone", number: event.target.value })
          }
        />
      )}
      {fieldSchema.description && (
        <p className="text-sm text-muted-foreground">
          {fieldSchema.description}
        </p>
      )}
    </div>
  );
}
